import { GroupProps } from '@react-three/fiber'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { Surface3D } from 'features/assetViz/objects/surfaces/Surface3D'
import { ComponentSurfaceWrappingMode } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { radialConeGeometry } from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

/**
 * A cone, plotted on a radial plane, with the center of its base at "position"
 * @constructor
 */
export const BasicConeSurface3D: React.FC<GroupProps> = (groupProps) => {
  //----------------------------------------------------------------------------

  const { focusedCDF } = useAssetVizInternal()

  const { bounds, scale } = React.useMemo(() => {
    const id = focusedCDF?.data.geometry.componentID ?? ''
    // radial plot should always start at radius 0 and x range should always span 0 -> 360
    const bounds = GeckoComponentRect.CreateFromXYs(
      id,
      0,
      0,
      360,
      focusedCDF?.data.geometry.y.end.num ?? 1,
      GeckoMeasurementUnit.Degrees,
      focusedCDF?.data.geometry.y.unit ?? GeckoMeasurementUnit.Feet
    )

    const scale = extractScale(groupProps.scale).multiply(
      new Vector3(bounds.y.end.num, bounds.y.end.num, 1.0)
    )
    return { bounds, scale }
  }, [focusedCDF, groupProps.scale])

  //----------------------------------------------------------------------------

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const radius = 1.05
      const radialXPosition = radius * -Math.sin((coord.num * Math.PI) / 180)
      const radialYPosition = radius * Math.cos((coord.num * Math.PI) / 180)
      return [radialXPosition, radialYPosition, 0]
    },
    []
  )

  //----------------------------------------------------------------------------

  return (
    <>
      <Surface3D
        geometry={radialConeGeometry}
        bounds={bounds}
        wrapMode={ComponentSurfaceWrappingMode.ComponentWrapsX}
        scale={scale}>
        {/* Circumferential (x) ruler ---------------------------------------- */}
        <Ruler3D
          anchor='center'
          start={bounds.start.x}
          end={bounds.end.x}
          includeTicks={true}
          includeStart={true}
          includeEnd={false}
          getTickPosition={positionXTick}
        />
      </Surface3D>
    </>
  )
}
